import React, { useState, useEffect } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { FileText, Landmark } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { dollarsToCents, todayISO, formatCents } from "@/lib/format";
import { toast } from "sonner";

const emptyInvoice = { client_name: "", reference_code: "", amount: "", issue_date: todayISO(), due_date: "", notes: "", recurring: false };
const emptyPayout = { platform_name: "", reference_code: "", amount: "", expected_date: "", notes: "" };

export function RecordSlideOver({ open, onOpenChange, onCreated, editRecord }) {
  const [type, setType] = useState(null); // 'invoice' | 'payout'
  const [inv, setInv] = useState(emptyInvoice);
  const [pay, setPay] = useState(emptyPayout);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editRecord) {
      setType(editRecord.type);
      if (editRecord.type === "invoice") {
        setInv({ client_name: editRecord.client_name, reference_code: editRecord.reference_code,
          amount: (editRecord.amount_cents / 100).toString(), issue_date: editRecord.issue_date,
          due_date: editRecord.due_date, notes: editRecord.notes || "", recurring: editRecord.recurring || false });
      } else {
        setPay({ platform_name: editRecord.platform_name, reference_code: editRecord.reference_code,
          amount: (editRecord.amount_cents / 100).toString(), expected_date: editRecord.expected_date,
          notes: editRecord.notes || "" });
      }
    } else {
      setType(null); setInv(emptyInvoice); setPay(emptyPayout);
    }
    setErrors({});
  }, [open, editRecord]);

  const validate = () => {
    const e = {};
    if (type === "invoice") {
      if (!inv.client_name.trim()) e.client_name = "Client name is required";
      if (!inv.amount || dollarsToCents(inv.amount) <= 0) e.amount = "Enter an amount";
      if (!inv.due_date) e.due_date = "Due date is required";
    } else {
      if (!pay.platform_name.trim()) e.platform_name = "Platform / source is required";
      if (!pay.amount || dollarsToCents(pay.amount) <= 0) e.amount = "Enter an amount";
      if (!pay.expected_date) e.expected_date = "Expected date is required";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const isValid = () => {
    if (type === "invoice") return inv.client_name.trim() && inv.amount && dollarsToCents(inv.amount) > 0 && inv.due_date;
    if (type === "payout") return pay.platform_name.trim() && pay.amount && dollarsToCents(pay.amount) > 0 && pay.expected_date;
    return false;
  };

  const submit = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      if (type === "invoice") {
        const body = { client_name: inv.client_name, reference_code: inv.reference_code || undefined,
          amount_cents: dollarsToCents(inv.amount), issue_date: inv.issue_date, due_date: inv.due_date, notes: inv.notes, recurring: inv.recurring };
        if (editRecord) await api.patch(`/invoices/${editRecord.id}`, body);
        else await api.post("/invoices", body);
      } else {
        const body = { platform_name: pay.platform_name, reference_code: pay.reference_code || undefined,
          amount_cents: dollarsToCents(pay.amount), expected_date: pay.expected_date, notes: pay.notes };
        if (editRecord) await api.patch(`/payouts/${editRecord.id}`, body);
        else await api.post("/payouts", body);
      }
      toast.success(editRecord ? "Record updated" : "Record added");
      onOpenChange(false);
      onCreated && onCreated();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const Field = ({ id, label, children, error }) => (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-xs font-semibold text-espresso-soft">{label}</Label>
      {children}
      {error && <p className="text-xs text-rose-600" data-testid={`error-${id}`}>{error}</p>}
    </div>
  );

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="record-slideover">
        <SheetHeader>
          <SheetTitle className="font-heading text-2xl">
            {editRecord ? "Edit record" : "What are you tracking?"}
          </SheetTitle>
        </SheetHeader>

        {!type && !editRecord && (
          <div className="grid grid-cols-1 gap-3 mt-6">
            <button data-testid="choose-invoice" onClick={() => setType("invoice")}
              className="flex items-center gap-4 p-5 border rounded-lg text-left hover:bg-oatmeal hover:border-warmborder transition-colors">
              <span className="p-2.5 bg-slate-900 rounded-md text-white"><FileText size={20} /></span>
              <span><span className="block font-semibold">Invoice</span>
                <span className="text-sm text-espresso-soft">Money a client owes you</span></span>
            </button>
            <button data-testid="choose-payout" onClick={() => setType("payout")}
              className="flex items-center gap-4 p-5 border rounded-lg text-left hover:bg-oatmeal hover:border-warmborder transition-colors">
              <span className="p-2.5 bg-slate-900 rounded-md text-white"><Landmark size={20} /></span>
              <span><span className="block font-semibold">Platform payout</span>
                <span className="text-sm text-espresso-soft">Expected deposit from a platform</span></span>
            </button>
          </div>
        )}

        {type === "invoice" && (
          <div className="space-y-4 mt-6">
            <Field id="client_name" label="Client" error={errors.client_name}>
              <Input id="client_name" data-testid="input-client" value={inv.client_name}
                onChange={(e) => setInv({ ...inv, client_name: e.target.value })} placeholder="North Coast Studio" />
            </Field>
            <Field id="reference_code" label="Invoice number (optional)">
              <Input id="reference_code" data-testid="input-invoice-ref" value={inv.reference_code}
                onChange={(e) => setInv({ ...inv, reference_code: e.target.value })} placeholder="Auto-generated if blank" />
            </Field>
            <Field id="amount" label="Amount" error={errors.amount}>
              <Input id="amount" data-testid="input-amount" value={inv.amount} inputMode="decimal"
                onChange={(e) => setInv({ ...inv, amount: e.target.value })} placeholder="1260.00" />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field id="issue_date" label="Issue date">
                <Input id="issue_date" type="date" data-testid="input-issue-date" value={inv.issue_date}
                  onChange={(e) => setInv({ ...inv, issue_date: e.target.value })} />
              </Field>
              <Field id="due_date" label="Due date" error={errors.due_date}>
                <Input id="due_date" type="date" data-testid="input-due-date" value={inv.due_date}
                  onChange={(e) => setInv({ ...inv, due_date: e.target.value })} />
              </Field>
            </div>
            <Field id="notes" label="Notes (optional)">
              <Textarea id="notes" data-testid="input-notes" value={inv.notes}
                onChange={(e) => setInv({ ...inv, notes: e.target.value })} rows={2} />
            </Field>
            <label className="flex items-start gap-2.5 cursor-pointer border rounded-lg p-3 hover:bg-oatmeal transition-colors">
              <Checkbox checked={inv.recurring} onCheckedChange={(v) => setInv({ ...inv, recurring: v })} className="mt-0.5" data-testid="recurring-checkbox" />
              <span className="text-sm text-espresso-soft">
                <span className="font-semibold text-midnight block">Repeat monthly (retainer)</span>
                Auto-create next month's invoice for this client.
              </span>
            </label>
          </div>
        )}

        {type === "payout" && (
          <div className="space-y-4 mt-6">
            <Field id="platform_name" label="Platform / source" error={errors.platform_name}>
              <Input id="platform_name" data-testid="input-platform" value={pay.platform_name}
                onChange={(e) => setPay({ ...pay, platform_name: e.target.value })} placeholder="Freelance Platform" />
            </Field>
            <Field id="reference_code" label="Reference (optional)">
              <Input id="reference_code" data-testid="input-payout-ref" value={pay.reference_code}
                onChange={(e) => setPay({ ...pay, reference_code: e.target.value })} placeholder="Auto-generated if blank" />
            </Field>
            <Field id="amount" label="Amount" error={errors.amount}>
              <Input id="amount" data-testid="input-amount" value={pay.amount} inputMode="decimal"
                onChange={(e) => setPay({ ...pay, amount: e.target.value })} placeholder="640.00" />
            </Field>
            <Field id="expected_date" label="Expected payout date" error={errors.expected_date}>
              <Input id="expected_date" type="date" data-testid="input-expected-date" value={pay.expected_date}
                onChange={(e) => setPay({ ...pay, expected_date: e.target.value })} />
            </Field>
            <Field id="notes" label="Notes (optional)">
              <Textarea id="notes" data-testid="input-notes" value={pay.notes}
                onChange={(e) => setPay({ ...pay, notes: e.target.value })} rows={2} />
            </Field>
          </div>
        )}

        {(type || editRecord) && (
          <div className="flex gap-3 mt-8">
            {!editRecord && (
              <Button variant="outline" onClick={() => setType(null)} data-testid="back-btn">Back</Button>
            )}
            <Button className="flex-1" onClick={submit} disabled={saving || !isValid()} data-testid="save-record-btn">
              {saving ? "Saving…" : editRecord ? "Save changes" : "Add record"}
            </Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
